import "firebase/functions";
import { firestore } from "services/firebase";
import { 
  collection, 
  doc, 
  setDoc, 
  getDocs,
  getDoc,
  writeBatch,
  updateDoc,
  onSnapshot
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { arrayUnion } from "firebase/firestore";
import { functions } from "services/firebase";
import {
  createProject,
  duplicateProjectData,
  createUser,
  createFeedbackDocumentId,
} from "database/Factory";

const addEmailToContactForm = async (email) => {
  const sendEmailFn = httpsCallable(functions, "sendEmail");
  try {
    await sendEmailFn({
      html: email,
      subject: "Stakeholders Email List",
      id: email,
      email: "skipped",
    });
  } catch (e) {
    console.log(e);
  }
};

const sendDeveloperFeedback = async ({ user, body }) => {
  const sendEmailFn = httpsCallable(functions, "sendEmail");

  try {
    await sendEmailFn({
      html: body,
      subject: "Stakeholders Developer Feedback",
      id: createFeedbackDocumentId(),
      email: user?.email,
    });

    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
};

const addUserToFirestore = async ({ user }) => {
  const userRef = doc(firestore, "users", user.uid);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    await setDoc(userRef, {
      uid: user.uid,
      name: user.displayName,
      email: user.email,
      client: VARIABLES.clientVersion,
      projectsComponent: {
        projectLinks: {},
        ordering: {
          projectIds: [],
        },
      },
    });
  }
};


const deleteUserFromFirestore = async () => {
  console.log("deleteUserFromFirestore ...");

  const deleteFn = httpsCallable(functions, "deleteUser");
  try {
    const response = await deleteFn();
    return response;
  } catch (e) {
    console.log(e);
    return null;
  }
};

const duplicateProjectFromFirestore = async ({ project }) => {
  console.log("duplicateProjectFromFirestore ...");

  const stakeholdersColRef = collection(
    firestore, 
    "users", 
    project.userId, 
    "projects", 
    project.id, 
    "stakeholders"
  );

  const snapshot = await getDocs(stakeholdersColRef);
  let stakeholders = snapshot.docs.map((doc) => doc.data());
  let [dupProject, dupStakeholders] = duplicateProjectData({
    project: project,
    stakeholders: stakeholders,
    userId: project.userId,
  });

  addProjectToFirestore({
    userId: project.userId,
    project: dupProject,
    stakeholders: dupStakeholders,
  });

  return dupProject;
};

const importLocalProjectIntoFirestore = ({ userId, project, stakeholders }) => {
  console.log("importLocalProjectIntoFirestore ...");

  let [dupProject, dupStakeholders] = duplicateProjectData({
    project: project,
    stakeholders: stakeholders,
    userId: userId,
  });

  dupProject.id = project.id;

  addProjectToFirestore({
    userId: userId,
    project: dupProject,
    projectId: project.id,
    stakeholders: dupStakeholders,
  });
};

const deleteProjectFromFirestore = async ({
  userId,
  projectId,
  projectIds,
}) => {
  console.log("deleteProjectFromFirestore ...");

  const stakeholdersRef = collection(
    firestore, 
    "users", 
    userId, 
    "projects", 
    projectId, 
    "stakeholders"
  );

  const userRef = doc(firestore, "users", userId);

  const batch = writeBatch(firestore);
  const snapshot = await getDocs(stakeholdersRef);

  snapshot.docs.forEach((docSnap) => {
    batch.delete(doc(firestore, "users", userId, "projects", docSnap.id));
  });

  batch.delete(doc(firestore, "users", userId, "projects", projectId));

  batch.update(userRef, {
    ["projectsComponent.ordering.projectIds"]: projectIds
  });

  await batch.commit();
};

const deleteSharedProjectLinkFromFirestore = async ({
  userId,
  projectLinkId,
  userData,
  userLinkId,
}) => {
  console.log("deleteSharedProjectLinkFromFirestore ...");

  const sharedProjectLinks = userData.projectsComponent.projectLinks;
  const projectLinkIds = sharedProjectLinks[userLinkId].filter(
    (projectId) => projectId !== projectLinkId
  );
  sharedProjectLinks[userLinkId] = projectLinkIds;

  const userRef = doc(firestore, "users", userId);
  await updateDoc(userRef, {
    ["projectsComponent.projectLinks"]: sharedProjectLinks,
  });
};

const updateExistingProjectInFirestore = async ({
  userId,
  projectId,
  project,
  silent,
}) => {
  console.log("updateExistingProjectInFirestore ...");

  if (!silent) {
    project.updated = Date.now();
  }

  const ref = doc(firestore, "users", userId, "projects", projectId);

  await updateDoc(ref, { ...project });
};

const updateExistingStakeholderInFirestore = async ({
  userId,
  projectId,
  updatedStakeholder,
}) => {
  console.log("updateExistingStakeholderInFirestore ....");

  
  const ref = doc(firestore, "users", userId, "projects", projectId, "stakeholders", updatedStakeholder.id);
  await updateDoc(ref, { ...updatedStakeholder });
};

const setProjectsInFirestore = async ({ userId, projects }) => {
  console.log("setProjectsInFirestore ...");

  const ref = collection(firestore, "users", userId, "projects");

  Object.entries(projects).forEach(([projectId, project]) => {
    const projectRef = doc(ref, projectId);
    setDoc(projectRef, project);
  });
};

const addProjectToFirestore = async ({
  userId,
  project,
  projectId,
  stakeholders,
}) => {
  // Handle adding the project

  console.log("addProjectToFirestore ... ");

  let newProject = null;
  if (project) {
    newProject = project;
  } else {
    newProject = createProject({ userId: userId, id: projectId });
  }

  var batch = writeBatch(firestore);

  const projectRef = doc(firestore, "users", userId, "projects", newProject.id);

  batch.set(projectRef, newProject);
  const userRef = doc(firestore, "users", userId);

  batch.set(
    userRef,
    {
      projectsComponent: {
        ordering: {
          projectIds: arrayUnion(newProject.id),
        },
      },
    },
    { merge: true }
  );

  if (stakeholders) {
    console.log("setting stakeholders");
    stakeholders.forEach((stakeholder) => {
      const stakeholderRef = doc(
        firestore, 
        "users", 
        userId, 
        "projects", 
        newProject.id,
        "stakeholders",
        stakeholder.id
      );
      batch.set(stakeholderRef, stakeholder);
    });
  }

  batch.commit();

  return newProject;
};

const addSharedProjectLinkToFirestore = ({
  userId,
  userLinkId,
  projectLinkId,
}) => {
  console.log("addSharedProjectLinkToFirestore ...");

  const userDocRef = doc(firestore, "users", userId);

  updateDoc(
    userDocRef,
    {
      [`projectsComponent.projectLinks.${userLinkId}`]: arrayUnion(projectLinkId)
    }
  );
};

const addProjectIdOrderingToFirestore = ({ userId, projectId, projectIds }) => {
  console.log("addProjectIdOrderingToFirestore");

  const ref = doc(firestore, "users", userId);

  updateDoc(
    ref,
    {
      ["projectsComponent.ordering.projectIds"]: arrayUnion(projectId)
    }
  );
};

const setProjectIdOrderingToFirestore = ({ userId, projectIds }) => {
  console.log("setProjectIdOrderingToFirestore");

  const ref = doc(firestore, "users", userId);

  updateDoc( 
    ref, 
    {
      ["projectsComponent.ordering.projectIds"]: projectIds
    }
  );
};

const getAllProjectsFromFirestore = ({ userId, observer }) => {
  console.log("getAllProjectsFromFirestore ...");

  const ref = onSnapshot(
    collection(firestore, "users", userId, "projects"),
    observer
  );
  return ref;
};

const getUserFromFirestore = ({ userId, observer }) => {
  console.log("getUserFromFirestore ...");
  const ref = onSnapshot(
    doc(firestore, "users", userId),
    observer
  );
  return ref;
};

const getSharedProjectLinksFromFireStore = async ({ userId, observer }) => {
  const ref = doc(firestore, "users", userId);

  const snapshotRef =  onSnapshot(
    ref,
    observer
  ) 

  return snapshotRef;
};

const getProjectFromFirestore = ({ userId, projectId, observer }) => {
  console.log("getProjectFromFirestore ...");

  const docRef = doc(firestore, "users", userId, "projects", projectId);

  const snapshotRef = onSnapshot( docRef, observer);
  return snapshotRef;
};

const getStakeholdersFromFirestore = ({ userId, projectId, observer }) => {
  console.log("getStakeholdersFromFirestore ...");

  const ref = onSnapshot(
    collection(firestore, "users", userId, "projects", projectId, "stakeholders"),
    observer
  );
  return ref;
};

const getStakeholdersFromFirestore_noObserver = async ({
  userId,
  projectId,
}) => {
  console.log("getStakeholdersFromFirestore_noObserver ...");

  const stakeholdersCollectionRef = collection(firestore, "users", userId, "projects", projectId, "stakeholders");
  const stakeholdersSnapshot = await getDocs(stakeholdersCollectionRef);


  return stakeholdersSnapshot.docs.map((doc) => doc.data());
};

const updateStakeholderInFirestore = async ({
  userId,
  projectId,
  stakeholder,
  project,
  formData
}) => {
  console.log("updateStakeholderInFirestore ...");

  const stakeholderDocRef = doc(
    firestore,
    "users",
    userId,
    "projects",
    projectId,
    "stakeholders",
    stakeholder.id
  );
  const projectDocRef = doc(
    firestore,
    "users",
    userId,
    "projects",
    projectId
  );
  var batch = writeBatch(firestore);

  if(formData) { /** New Stakeholder (AI or Guided) form submission */
    if(stakeholder.aiPowered) { /** AI Powered */
      delete formData.aiPowered;
      console.log("Firestore.js: short form ", formData);
      formData.docType = "formSubmissionData";
      const aiRequestDocRef = doc(
        firestore,
        "users",
        userId,
        "projects",
        projectId,
        "stakeholders",
        stakeholder.id,
        "airequests",
        formData.id,
      );

      project.updated = Date.now();

      batch.set(stakeholderDocRef, stakeholder);
      batch.set(projectDocRef, project);
      batch.set(aiRequestDocRef, formData);
    
    } else { /** Guided Assessment */
      const formData = stakeholder.formDataObj;

      const aiRequestDocRef = doc(
        firestore,
        "users",
        userId,
        "projects",
        projectId,
        "stakeholders",
        stakeholder.id,
        "airequests",
        formData.id,
      );

      batch.set(aiRequestDocRef, formData);
    }
  }
  else { /** Start from scratch */
  
    project.updated = Date.now();

    var batch = writeBatch(firestore);

    batch.set(stakeholderDocRef, stakeholder);
    batch.set(projectDocRef, project);
  }

  console.log("Firestore.js: short form about to submit");
  await batch.commit();
  return stakeholderDocRef;
};

const aiNewStakeholderRequestFnc = async (requestData) => {
  console.log("aiNewStakeholderRequestFnc ...", requestData);

  const fullName = requestData.info.fullName;

  const formData = {
    info: { ...requestData.info },
    project: requestData.project,
  };
  if(requestData.otherProjectStakeholders?.length > 0) formData.otherProjectStakeholders = requestData.otherProjectStakeholders;

  delete formData.info.fullName;

  const requestOpenAIFn = httpsCallable(functions, "aiRequest");
  try {
    console.log("formData:", formData);
    const response = await requestOpenAIFn(formData);
    console.log("aiNewStakeholderRequestFnc(): response: ",response);
    response.data.data.relationship = Math.round(((response.data.data.relationship - 1) / 99) * 100) - 50;
    console.log("response.data.data.relationship",response.data.data.relationship);
    return {
      success: true,
      data: response.data.data,
      name: fullName,
      aiCount: response.data.data.aiCount,
    };

  } catch (e) {
    console.log("FAIL: requestOpenAI", e);

    return {
      success: false,
      error: {
        code: e?.code,
        message: e?.message,
      },
      aiUsage: e?.details?.aiUsage,
    };
  }
};

const aiStakeholdersCount = async (userId) => {
  console.log("aiStakeholdersCount ...");
  console.log("aiStakeholdersCount()",userId);

  const aiDataRef = doc(
    firestore,
    "users",
    userId,
    "ai",
    "data"
  );

  const snapshot = await getDoc(aiDataRef);

  if (!snapshot.exists()) {
    return 0;
  }
  let count = await snapshot.data().aiRequests.count;
  console.log("Firestore.js, aiStakeholdersCount",count);
  return count ?? 0;
};


const deleteStakeholderInFirestore = async ({ userId, stakeholderId, project }) => {
  console.log("removeStakeholderInFirestore ...");

  const stakeholderDocRef = doc(
    firestore,
    "users",
    userId,
    "projects",
    project.id,
    "stakeholders",
    stakeholderId
  );

  const projectDocRef = doc(
    firestore,
    "users",
    userId,
    "projects",
    project.id
  );

  project.updated = Date.now();

  var batch = writeBatch(firestore);
  batch.delete(stakeholderDocRef);
  batch.set(projectDocRef, project);
  await batch.commit();
};

const addManyStakeholdersToFirestore = async ({ project, stakeholders }) => {
  console.log("addManyStakeholdersToFirestore ...");

  const projectRef = doc(
    firestore,
    "users",
    project.userId,
    "projects",
    project.id
  );

  var batch = writeBatch(firestore);
  stakeholders.forEach((stakeholder) => {
    const stakeholdersRef = doc(
      firestore,
      "users",
      project.userId,
      "projects",
      project.id,
      "stakeholders",
      stakeholder.id
    );

    batch.set(stakeholdersRef, stakeholder);
  });

  batch.update(projectRef, { ...project });
  await batch.commit();
};

const setCollaborationInFirestore = ({ userId, projectId, sharing }) => {
  console.log("setCollaborationInFirestore ...");

  const projectDocRef = doc(firestore, "users", userId, "projects", projectId);

  updateDoc( projectDocRef, 
    {
      ["sharing"]: sharing
    }
  );
};

export {
  duplicateProjectFromFirestore,
  deleteProjectFromFirestore,
  updateExistingProjectInFirestore,
  setProjectsInFirestore,
  addProjectToFirestore,
  getAllProjectsFromFirestore,
  getProjectFromFirestore,
  getStakeholdersFromFirestore,
  addUserToFirestore,
  deleteStakeholderInFirestore,
  updateExistingStakeholderInFirestore,
  getSharedProjectLinksFromFireStore,
  deleteSharedProjectLinkFromFirestore,
  addSharedProjectLinkToFirestore,
  updateStakeholderInFirestore,
  getUserFromFirestore,
  addProjectIdOrderingToFirestore,
  setProjectIdOrderingToFirestore,
  importLocalProjectIntoFirestore,
  deleteUserFromFirestore,
  addEmailToContactForm,
  aiNewStakeholderRequestFnc,
  aiStakeholdersCount,
  sendDeveloperFeedback,
  getStakeholdersFromFirestore_noObserver,
  addManyStakeholdersToFirestore,
  setCollaborationInFirestore,
};
